import React, { useState, useEffect } from 'react';
import { RiskCard, RiskCardHeader, RiskCardBody } from './UiKit';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

const METHODOLOGIES = [
  {
    id: 'likelihood_impact_5x5',
    name: '5×5 Likelihood × Impact',
    desc: 'Standard risk matrix used by most Medicare Advantage compliance programs. Rates likelihood (1-5) and impact (1-5). Score range 1-25.',
    icon: '📊',
    recommended: true
  },
  {
    id: 'rpn_fmea',
    name: 'RPN / FMEA',
    desc: 'Risk Priority Number via Failure Mode & Effects Analysis. Rates severity (1-10), occurrence (1-10), and detection difficulty (1-10). Score range 1-1000.',
    icon: '🔬'
  },
  {
    id: 'inherent_residual',
    name: 'Inherent / Residual',
    desc: 'Three-dimensional inherent risk with control strength adjustment. Rates severity, likelihood, exposure (1-5) and control design/operating (0-4). Score range 1-125.',
    icon: '📐'
  }
];

const RPN_SCALES = [
  { v: 3, label: '1–3', desc: 'Compact. Max RPN 27.' },
  { v: 5, label: '1–5', desc: 'Mid. Max RPN 125.' },
  { v: 10, label: '1–10', desc: 'Classic FMEA. Max RPN 1000.' },
];

export default function ScoringMethodologySelector() {
  const [current, setCurrent] = useState('likelihood_impact_5x5');
  const [rpnScale, setRpnScale] = useState(10);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_BASE}/risk-assessment/organization/settings`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (data.success && data.settings?.scoring_methodology) {
          setCurrent(data.settings.scoring_methodology);
        }
        if (data.success && data.settings?.rpn_scale) {
          setRpnScale(Number(data.settings.rpn_scale));
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };
    fetchSettings();
  }, []);

  const handleScale = async (scale) => {
    if (scale === rpnScale) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/organization/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rpn_scale: scale }),
      });
      const data = await res.json();
      if (data.success) {
        setRpnScale(scale);
        setMessage('RPN scale updated');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.message || 'Failed to update');
      }
    } catch (err) {
      setMessage('Network error');
    }
    setSaving(false);
  };

  const handleSelect = async (methodologyId) => {
    if (methodologyId === current) return;
    setSaving(true);
    setMessage('');

    try {
      const res = await fetch(`${API_BASE}/risk-assessment/organization/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scoring_methodology: methodologyId })
      });
      const data = await res.json();
      if (data.success) {
        setCurrent(methodologyId);
        setMessage('Scoring methodology updated');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.message || 'Failed to update');
      }
    } catch (err) {
      setMessage('Network error');
    }
    setSaving(false);
  };

  return (
    <RiskCard>
      <RiskCardHeader
        title="Scoring Methodology"
        subtitle="Select the risk scoring methodology for your organization"
      />
      <RiskCardBody>
        {message && (
          <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
            message.includes('updated') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message}
          </div>
        )}

        <p className="mb-4 text-sm text-slate-500">
          This choice decides which factors graders are asked for across intake, the register and
          scoring. Changing it does not alter or delete any score already recorded, but risks scored
          under the previous methodology will not show a score under the new one until they are
          regraded, because the factors differ.
        </p>

        <div className="space-y-3">
          {METHODOLOGIES.map(m => (
            <button
              key={m.id}
              onClick={() => handleSelect(m.id)}
              disabled={saving}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                current === m.id
                  ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              } ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{m.name}</span>
                    {m.recommended && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full uppercase">
                        Recommended
                      </span>
                    )}
                    {current === m.id && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full uppercase">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{m.desc}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* RPN factor scale — only relevant when RPN/FMEA is active */}
        {current === 'rpn_fmea' && (
          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="font-semibold text-slate-900 mb-1">RPN factor scale</div>
            <p className="text-sm text-slate-500 mb-3">
              Rate severity, occurrence, and detection on this scale. Bands follow the standard FMEA
              classification (action at 10% / 30% / 50% of the maximum).
            </p>
            <div className="grid grid-cols-3 gap-3">
              {RPN_SCALES.map(s => (
                <button
                  key={s.v}
                  onClick={() => handleScale(s.v)}
                  disabled={saving}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${
                    rpnScale === s.v
                      ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  } ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <div className="font-bold text-slate-900">{s.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </RiskCardBody>
    </RiskCard>
  );
}
